/**
 * DailyUpdateStore — the daily-updates feature's data-access wrapper.
 *
 * Strict layering: this is the ONLY dailyUpdates module that talks to the API,
 * and it does so exclusively through the {@link EngineeringVectorApi} facade
 * (`api.documents<DailyUpdate>('dailyUpdates')` + `api.identityProvider`). It
 * never imports ApiClient or calls fetch. React hooks call this store; pages
 * call the hook.
 *
 * Persistence model: each update is one document with id `du_<uuid>` in the
 * real 'dailyUpdates' generic-store collection (DDB pointer row + S3 body).
 * Every field is short, so — like Tasks — every field is mirrored onto the DDB
 * pointer row's index attributes and {@link listSummaries} reconstructs full
 * updates directly from the pointer rows WITHOUT hydrating S3 bodies.
 *
 * Deletion: the API layer exposes no delete op, so {@link softDelete} sets a
 * `deleted` flag on the document and its pointer row; queries filter it out.
 *
 * NO backend changes are required: this reuses the generic document store.
 */

import type { EngineeringVectorApi } from '../../api';
import type { DocumentStore } from '../../api';
import type { StringMap } from '../../api';
import type {
  DailyUpdate,
  DailyUpdateDraft,
  DailyUpdateSummary,
} from './types';
import { dailyUpdateDocId } from './dailyUpdateModel';

export const DAILY_UPDATES_COLLECTION = 'dailyUpdates';

/** Pointer-row index attribute keys. */
const ROW = {
  primaryIdentifier: 'primaryIdentifier',
  collection: 'collection',
  engineer: 'engineer',
  date: 'date',
  storyId: 'storyId',
  workstreamId: 'workstreamId',
  goalId: 'goalId',
  yesterday: 'yesterday',
  today: 'today',
  resultLearning: 'resultLearning',
  blocker: 'blocker',
  createdAt: 'createdAt',
  createdBy: 'createdBy',
  deleted: 'deleted',
} as const;

export class DailyUpdateStore {
  private readonly docs: DocumentStore<DailyUpdate>;

  constructor(private readonly api: EngineeringVectorApi) {
    this.docs = api.documents<DailyUpdate>(DAILY_UPDATES_COLLECTION);
  }

  /** Builds the queryable index attributes mirrored onto the pointer row. */
  private indexAttributes(update: DailyUpdate): StringMap {
    return {
      [ROW.engineer]: update.engineer ?? '',
      [ROW.date]: update.date ?? '',
      [ROW.storyId]: update.storyId ?? '',
      [ROW.workstreamId]: update.workstreamId ?? '',
      [ROW.goalId]: update.goalId ?? '',
      [ROW.yesterday]: update.yesterday ?? '',
      [ROW.today]: update.today ?? '',
      [ROW.resultLearning]: update.resultLearning ?? '',
      [ROW.blocker]: update.blocker ?? '',
      [ROW.createdAt]: update.createdAt ?? '',
      [ROW.createdBy]: update.createdBy ?? '',
      [ROW.deleted]: update.deleted ? 'true' : 'false',
    };
  }

  /** Reconstructs a full update projection from a pointer row. */
  private toSummary(row: StringMap): DailyUpdateSummary {
    return {
      id: row[ROW.primaryIdentifier] ?? '',
      engineer: row[ROW.engineer] ?? '',
      date: row[ROW.date] ?? '',
      storyId: row[ROW.storyId] || undefined,
      workstreamId: row[ROW.workstreamId] || undefined,
      goalId: row[ROW.goalId] || undefined,
      yesterday: row[ROW.yesterday] || undefined,
      today: row[ROW.today] || undefined,
      resultLearning: row[ROW.resultLearning] || undefined,
      blocker: row[ROW.blocker] || undefined,
      createdAt: row[ROW.createdAt] ?? '',
      createdBy: row[ROW.createdBy] ?? '',
    };
  }

  /**
   * Lists all non-deleted update summaries. Filters to this collection
   * defensively (the underlying list is not collection-scoped).
   */
  async listSummaries(): Promise<DailyUpdateSummary[]> {
    const rows = await this.docs.list();
    return rows
      .filter(
        (r) =>
          r[ROW.collection] === DAILY_UPDATES_COLLECTION &&
          r[ROW.deleted] !== 'true' &&
          !!r[ROW.primaryIdentifier],
      )
      .map((r) => this.toSummary(r));
  }

  /** Loads a single update document by id (hydrates the S3 body). */
  async get(id: string): Promise<DailyUpdate | undefined> {
    return this.docs.get(id);
  }

  /**
   * Creates a new update. Seeds id, createdAt, and createdBy (from identity,
   * defaulting the engineer field when blank), and returns the saved document.
   */
  async create(draft: DailyUpdateDraft): Promise<DailyUpdate> {
    const identity = await this.api.identityProvider.getIdentity();
    const update = this.fromDraft(
      dailyUpdateDocId(),
      draft,
      identity.alias,
      undefined,
    );
    await this.docs.save(update.id, update, this.indexAttributes(update));
    return update;
  }

  /**
   * Applies a draft's fields to an existing update in place (same id/createdBy/
   * createdAt) and re-mirrors index attributes.
   */
  async applyDraft(
    update: DailyUpdate,
    draft: DailyUpdateDraft,
  ): Promise<DailyUpdate> {
    const updated = this.fromDraft(
      update.id,
      draft,
      update.createdBy,
      update.createdAt,
    );
    return this.update(updated);
  }

  /** Persists a full update document in place. */
  async update(update: DailyUpdate): Promise<DailyUpdate> {
    const updated: DailyUpdate = { ...update, deleted: false };
    await this.docs.update(updated.id, updated, this.indexAttributes(updated));
    return updated;
  }

  /**
   * Soft-deletes an update: fetches it, flags it deleted, and updates both the
   * S3 body and the pointer row so queries filter it out.
   */
  async softDelete(id: string): Promise<void> {
    const existing = await this.docs.get(id);
    if (!existing) return;
    const deleted: DailyUpdate = { ...existing, deleted: true };
    await this.docs.update(id, deleted, this.indexAttributes(deleted));
  }

  /**
   * Builds a full DailyUpdate from a draft, seeding id/createdBy and preserving
   * createdAt on edit (new entries stamp "now"). Empty text fields collapse to
   * undefined so the pointer row stays lean.
   */
  private fromDraft(
    id: string,
    draft: DailyUpdateDraft,
    createdBy: string,
    existingCreatedAt: string | undefined,
  ): DailyUpdate {
    const clean = (s?: string) => {
      const t = (s ?? '').trim();
      return t ? t : undefined;
    };
    return {
      id,
      engineer: draft.engineer.trim() || createdBy,
      date: draft.date.trim(),
      storyId: draft.storyId || undefined,
      workstreamId: draft.workstreamId || undefined,
      goalId: draft.goalId || undefined,
      yesterday: clean(draft.yesterday),
      today: clean(draft.today),
      resultLearning: clean(draft.resultLearning),
      blocker: clean(draft.blocker),
      createdAt: existingCreatedAt ?? new Date().toISOString(),
      createdBy,
      deleted: false,
    };
  }
}
